import { useState, useEffect, useCallback } from "react";
import { Drawer, Skeleton, Tooltip } from "antd";
import { DownOutlined, RightOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { fetchClientEmailLogs } from "../../../services/customer360Service";
import {
  dateTime, relative, statusChip, chip, TONE, HtmlPreview, PanelEmpty, PanelError,
} from "./shared";

/**
 * Everything sent to one client, or to one client group.
 *
 * A drawer rather than a modal: this is opened from a row to look something up
 * and then closed, and a drawer leaves the table it came from visible behind it.
 *
 * Bodies stay collapsed until asked for. A list of twenty emails each rendering
 * its own iframe would be twenty documents parsed to show one.
 */
const EmailLogsDrawer = ({ userId, recipient, recipientType, onClose }) => {
  const [state, setState] = useState({ rows: null, loading: false, error: null });
  const [openId, setOpenId] = useState(null);

  const load = useCallback(() => {
    if (!recipient) return;
    setState({ rows: null, loading: true, error: null });
    fetchClientEmailLogs(userId, recipient.id, recipientType)
      .then((res) => setState({ rows: res?.data || [], loading: false, error: null }))
      .catch((error) => setState({ rows: null, loading: false, error }));
  }, [userId, recipient, recipientType]);

  /* Fetched per recipient, and only while one is open. Each row is a different
     mailbox, so nothing here can be cached across them. */
  useEffect(() => {
    setOpenId(null);
    load();
  }, [load]);

  const rows = state.rows || [];

  return (
    <Drawer
      open={Boolean(recipient)}
      onClose={onClose}
      width={720}
      title={(
        <div>
          <div style={{ fontWeight: 700, fontSize: 15 }}>Emails sent</div>
          <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 400, marginTop: 2 }}>
            {recipient?.name || recipient?.client_name || "This recipient"}
            {recipient?.email ? ` · ${recipient.email}` : ""}
          </div>
        </div>
      )}
      styles={{ body: { padding: 16, background: "#f8fafc" } }}
    >
      {state.loading && <Skeleton active paragraph={{ rows: 6 }} />}
      {state.error && <PanelError error={state.error} onRetry={load} />}

      {!state.loading && !state.error && rows.length === 0 && (
        <PanelEmpty message="No emails have been sent to this recipient yet." />
      )}

      {!state.loading && !state.error && rows.map((mail, i) => {
        const id = mail.id ?? i;
        const open = openId === id;
        return (
          <div
            key={id}
            style={{
              background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0",
              marginBottom: 10, overflow: "hidden",
            }}
          >
            <button
              onClick={() => setOpenId(open ? null : id)}
              style={{
                width: "100%", display: "flex", alignItems: "flex-start", gap: 10,
                padding: "13px 15px", border: "none", background: "transparent",
                cursor: "pointer", textAlign: "left",
              }}
            >
              <span style={{ color: "#94a3b8", fontSize: 11, marginTop: 3 }}>
                {open ? <DownOutlined /> : <RightOutlined />}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: "#0f172a", marginBottom: 3 }}>
                  {mail.subject || "(no subject)"}
                </div>
                <div style={{ fontSize: 12, color: "#64748b", wordBreak: "break-all" }}>
                  {mail.to_email || "—"}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 7, flexWrap: "wrap" }}>
                  {statusChip(mail.status)}
                  {/* Only when it was actually opened. A "not opened" chip on
                      every other row says the same nothing on a mail that failed
                      to send as on one the reader ignored — and an email that
                      never left cannot be opened at all. */}
                  {mail.opened_at && (
                    <Tooltip title={`Opened ${dateTime(mail.opened_at)}`}>
                      <span>{chip(<><CheckCircleOutlined /> Opened</>, TONE.green)}</span>
                    </Tooltip>
                  )}
                  {/* Same rule: no send time, nothing to print. */}
                  {mail.sent_at && (
                    <Tooltip title={dateTime(mail.sent_at)}>
                      <span style={{ fontSize: 11.5, color: "#94a3b8" }}>{relative(mail.sent_at)}</span>
                    </Tooltip>
                  )}
                </div>
              </div>
            </button>

            {open && (
              <div style={{ padding: "0 15px 15px" }}>
                <HtmlPreview
                  html={mail.html_content}
                  height={420}
                  title={`Email ${id}`}
                  empty="No preview — this email was logged without its body."
                />
              </div>
            )}
          </div>
        );
      })}
    </Drawer>
  );
};

export default EmailLogsDrawer;
